import { MapPinIcon } from '@heroicons/react/24/outline';
import { HotelForm as Form } from 'hotel/HotelForm';
import { Field } from 'react-final-form';

import { useUserRole } from '../hooks/useUserRole';
import {
  getFormattedToday,
  getFormattedTomorrow,
  validateDates,
  validateRoomBasedForm,
} from '../utilities/validators';

import { TealButton } from './Button';
import { BookingTypeTab } from './Inputs/BookingTypeTab';
import EnhancedDateRangePicker from './Inputs/FormInputs/EnhancedDateRangePicker';
import EnhancedNumberPicker from './Inputs/FormInputs/EnhancedNumberPicker';
import PlacesAutocomplete from './PlacesAutocomplete';

const maxInstantGuests = 16;
const maxInstantRooms = 8;
const minGroupRooms = 9;

type InitialValues = {
  bookingType: 'group' | 'instant';
  city?: {
    latitude: number;
    longitude: number;
    description?: string;
  };
  checkInDate: string;
  checkOutDate: string;
  guestQuantity: string;
  roomQuantity: string;
};

export interface FormValues {
  bookingType: 'group' | 'instant';
  city?: {
    latitude: number;
    longitude: number;
    description?: string;
  };
  checkInDate: string;
  checkOutDate: string;
  roomQuantity: string;
  guestQuantity: string;
}

interface MainHotelSearchFormProps {
  onSubmit?: (values: FormValues) => void;
  onChange?: (values: FormValues) => void;
  initialValues?: InitialValues | null;
  disabled?: boolean;
}
const isFormComplete = (values: FormValues, isOnlyTraveler: boolean) => {
  const {
    city,
    checkInDate,
    checkOutDate,
    guestQuantity,
    roomQuantity,
    bookingType,
  } = values;

  const hasValidLocation = city && city.latitude !== 0 && city.longitude !== 0;
  const hasValidDates = checkInDate && checkOutDate;

  // Pure travelers (without admin roles) need location, dates, and guest count (room fixed at 1)
  if (isOnlyTraveler) {
    return hasValidLocation && hasValidDates && !!guestQuantity;
  }

  return (
    hasValidLocation &&
    hasValidDates &&
    ((bookingType === 'instant' && guestQuantity && roomQuantity) ||
      (bookingType === 'group' && roomQuantity))
  );
};

const HotelSearchForm = ({
  onSubmit,
  disabled = false,
  initialValues = {
    bookingType: 'instant',
    city: {
      latitude: 0,
      longitude: 0,
    },
    checkInDate: '',
    checkOutDate: '',
    guestQuantity: '1',
    roomQuantity: '1',
  },
}: MainHotelSearchFormProps) => {
  // Use isOnlyTraveler (not isTraveler) to correctly handle users with dual roles (e.g., admin+traveler)
  const { isOnlyTraveler, canBookMultipleRooms, canCreateGroupRequests } =
    useUserRole();

  const processedInitialValues = { ...initialValues };

  if (
    initialValues &&
    initialValues.checkInDate &&
    initialValues.checkOutDate
  ) {
    try {
      const checkInDate = new Date(initialValues.checkInDate);
      const checkOutDate = new Date(initialValues.checkOutDate);

      if (checkOutDate <= checkInDate) {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        processedInitialValues.checkInDate = today.toISOString().split('T')[0];
        processedInitialValues.checkOutDate = tomorrow
          .toISOString()
          .split('T')[0];
      }
    } catch {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      processedInitialValues.checkInDate = today.toISOString().split('T')[0];
      processedInitialValues.checkOutDate = tomorrow
        .toISOString()
        .split('T')[0];
    }
  }

  const today = getFormattedToday();

  const formInitialValues = {
    // Pure travelers can only do instant booking
    bookingType: isOnlyTraveler
      ? 'instant'
      : initialValues?.bookingType || 'instant',
    city: initialValues?.city || undefined,
    checkInDate:
      initialValues?.checkInDate && initialValues.checkInDate >= today
        ? initialValues.checkInDate
        : today,
    checkOutDate:
      initialValues?.checkOutDate && initialValues.checkOutDate > today
        ? initialValues.checkOutDate
        : getFormattedTomorrow(),
    // Pure travelers book one room for themselves.
    roomQuantity: isOnlyTraveler
      ? '1'
      : initialValues?.roomQuantity ||
        (initialValues?.bookingType === 'group' ? String(minGroupRooms) : '1'),
    guestQuantity: isOnlyTraveler ? '1' : initialValues?.guestQuantity || '1',
  };

  const shouldSwitchToGroup = (values: FormValues) => {
    return (
      values.bookingType == 'instant' &&
      (Number(values.guestQuantity || 1) > maxInstantGuests ||
        Number(values.roomQuantity || 1) > maxInstantRooms)
    );
  };

  const shouldSwitchToInstant = (values: FormValues) => {
    return (
      values.bookingType == 'group' &&
      Number(values.roomQuantity || 1) < minGroupRooms
    );
  };

  const canSearchButton = (values: FormValues) => {
    if (values.bookingType == 'instant') {
      return !shouldSwitchToGroup(values);
    } else {
      return !shouldSwitchToInstant(values);
    }
  };

  return (
    <Form
      onSubmit={onSubmit || (() => {})}
      initialValues={formInitialValues}
      validate={(values) => ({
        ...validateDates(values),
        ...validateRoomBasedForm(values),
      })}
      subscription={{ values: true, errors: true, touched: true }}
      render={({ handleSubmit, values, errors, touched, form }) => {
        const handleSwitchToGroup = () => {
          form.change('bookingType', 'group');
        };

        return (
          <form
            onSubmit={handleSubmit}
            className={`flex h-full flex-col overflow-hidden rounded-lg border border-backgroundDarkGray bg-white px-4 pb-6 pt-2 ${disabled ? 'pointer-events-none opacity-60' : ''}`}
          >
            <div className="mb-4 transition-all duration-300 ease-in-out">
              {/* Mobile dropdown - only show toggle if group booking is available */}
              {canCreateGroupRequests && (
                <div className="grid grid-cols-1 sm:hidden">
                  <div className="flex items-center justify-between border-b border-gray-200 py-4">
                    <span className="text-base text-gray-900">
                      {values.bookingType === 'instant'
                        ? 'Instant Booking'
                        : 'Group Trips'}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const newType =
                          values.bookingType === 'instant'
                            ? 'group'
                            : 'instant';
                        form.change('bookingType', newType);
                      }}
                      className="text-sm text-primaryBlue hover:text-blue-900"
                    >
                      Switch to{' '}
                      {values.bookingType === 'instant'
                        ? 'Group Trips'
                        : 'Instant Booking'}
                    </button>
                  </div>
                </div>
              )}

              {/* Mobile label for travelers (no toggle) */}
              {!canCreateGroupRequests && (
                <div className="grid grid-cols-1 sm:hidden">
                  <div className="flex items-center border-b border-gray-200 py-4">
                    <span className="text-base text-gray-900">
                      Instant Booking
                    </span>
                  </div>
                </div>
              )}

              <BookingTypeTab
                name="bookingType"
                showGroupBooking={canCreateGroupRequests}
              />

              {canCreateGroupRequests && shouldSwitchToGroup(values) && (
                <p className="mt-4 text-base text-orange-700">
                  Instant booking is limited to trips with 16 guests or fewer
                  and up to 8 rooms. For larger bookings, please use our Group
                  Trip option.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
              <div className="w-full lg:flex-1 lg:grow">
                <Field
                  name="city"
                  placeholder="City"
                  component={PlacesAutocomplete}
                  icon={<MapPinIcon className="size-5 text-gray-400" />}
                  className="w-full"
                />
              </div>

              <div className="w-full lg:flex-1 lg:grow">
                <EnhancedDateRangePicker
                  meta={{ touched: false, error: '' }}
                  minDate={new Date()}
                  className="w-full"
                />

                {touched?.checkOutDate && errors?.checkOutDate && (
                  <div className="mt-1 text-sm text-red-500">
                    {errors.checkOutDate}
                  </div>
                )}
              </div>

              {values.bookingType === 'instant' && (
                <div className="w-full lg:w-32">
                  <EnhancedNumberPicker
                    name="guestQuantity"
                    labelText="Guest"
                    min={1}
                    max={100}
                    placeholder="Guests"
                    disabled={isOnlyTraveler}
                  >
                    {canBookMultipleRooms && shouldSwitchToGroup(values) && (
                      <div className="border-t border-gray-200 p-4">
                        <p className="text-sm">Have a large group?</p>
                        <button
                          onClick={handleSwitchToGroup}
                          className="mt-2 w-full rounded-md bg-secondaryBlue px-3 py-2 text-white"
                        >
                          Group Trip
                        </button>
                      </div>
                    )}
                  </EnhancedNumberPicker>
                </div>
              )}

              {canBookMultipleRooms && (
                <div className="w-full lg:w-32">
                  <EnhancedNumberPicker
                    name="roomQuantity"
                    labelText="Room"
                    min={values.bookingType === 'group' ? minGroupRooms : 1}
                    max={100}
                    placeholder="Rooms"
                  >
                    {values.bookingType === 'instant' &&
                      shouldSwitchToGroup(values) && (
                        <div className="border-t border-gray-200 p-4">
                          <p className="text-sm">Have a large group?</p>
                          <button
                            onClick={handleSwitchToGroup}
                            className="mt-2 w-full rounded-md bg-secondaryBlue px-3 py-2 text-white"
                          >
                            Group Trip
                          </button>
                        </div>
                      )}
                    {values.bookingType === 'group' &&
                      shouldSwitchToInstant(values) && (
                        <div className="border-t border-gray-200 p-4">
                          <p className="text-sm">Need fewer rooms?</p>
                          <button
                            onClick={() =>
                              form.change('bookingType', 'instant')
                            }
                            className="mt-2 w-full rounded-md bg-secondaryBlue px-3 py-2 text-white"
                          >
                            Instant Booking
                          </button>
                        </div>
                      )}
                  </EnhancedNumberPicker>
                </div>
              )}

              <div className="w-full lg:w-auto">
                <TealButton
                  text="Let's Go"
                  type="submit"
                  disabled={
                    !isFormComplete(values, isOnlyTraveler) ||
                    !canSearchButton(values) ||
                    Object.keys(errors || {}).length > 0
                  }
                  className="w-full px-4 lg:w-auto"
                />
              </div>
            </div>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out`}
              style={{
                maxHeight: Object.values(errors || {}).length > 0 ? 200 : 0,
                marginTop: Object.values(errors || {}).length > 0 ? 16 : 0,
              }}
            >
              {Object.values(errors || {}).length > 0 && (
                <ul className="list-disc pl-5 text-sm text-red-500">
                  {Object.entries(errors || {}).map(([key, error]) => (
                    <li key={key}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          </form>
        );
      }}
    />
  );
};

export default HotelSearchForm;
